import api from './api';

// Легасі-фінанси (окрема БД elchukof_fin, api/src/modules/finance-legacy/) -
// окрема група маршрутів /finance-legacy (не /admin/*). Здебільшого
// read-only, ЛИШЕ createEntry/updateEntry/deleteEntry (sourceApp=web,
// 2026-08-21) пишуть - легасі-записи (sourceApp=legacy) незмінні. Деталі:
// workspace/current-work/finance-legacy-integration.md

// entries.type: 1 = розхід, 2 = дохід - немає довідникової таблиці в дампі,
// конвенція підтверджена користувачем на живому перегляді (2026-08-20).
export const ENTRY_TYPE_EXPENSE = 1;
export const ENTRY_TYPE_INCOME = 2;

// 'legacy' (за замовчуванням, старий інтерфейс) | 'web' (створено/редаговано
// в нас) - редагування з вебдодатку доступне лише для власних (WEB) записів.
export const SOURCE_APP_LEGACY = 'legacy';
export const SOURCE_APP_WEB = 'web';

export interface FinanceLegacyEntry {
  id: number;
  type: number;
  projectId: number | null;
  sourceId: number;
  empId: number | null;
  orgId: number | null;
  currencyId: number;
  amount: number | null;
  amountUah: number | null;
  amountUsd: number | null;
  comment: string | null;
  createdUserId: number | null;
  createdAt: string;
  updatedAt: string;
  isDeleted: number;
  sourceApp: string;
}

// Тіло створення/редагування запису (вікно "Витрати"/"Доходи", 2026-08-21).
// createdUserId - ОБОВʼЯЗКОВИЙ: юзер вебдодатку явно обирає юзера легасі-бази,
// від імені якого пишеться запис (без автомапінгу на залогіненого адміна).
export interface CreateEntryPayload {
  type: number;
  projectId?: number;
  sourceId: number;
  empId?: number;
  currencyId: number;
  amount: number;
  comment?: string;
  createdUserId: number;
  createdAt?: string;
}

// FIX (код-рев'ю, 2026-08-21): projectId/empId/comment тепер явно `| null` -
// на PATCH `undefined` = "не чіпати поле", а `null` = "очистити". До фіксу
// очищення поля на формі (значення → '') мапилось в undefined, і бек мовчки
// лишав старе значення - користувач бачив "збережено", але поле не мінялось.
export type UpdateEntryPayload = Partial<Omit<CreateEntryPayload, 'type' | 'projectId' | 'empId' | 'comment'>> & {
  projectId?: number | null;
  empId?: number | null;
  comment?: string | null;
};

export interface FinanceLegacyOption {
  value: number;
  label: string;
}

// `type` на FinanceLegacyOption.sources (2026-08-21) - entry_sources.type
// корелює 1:1 з entries.type (перевірено на даних), форма створення фільтрує
// список джерел за напрямком "Витрати"/"Доходи".
export interface FinanceLegacyOptions {
  projects: FinanceLegacyOption[];
  sources: (FinanceLegacyOption & { type: number })[];
  employees: FinanceLegacyOption[];
  currencies: FinanceLegacyOption[];
}

export interface GetEntriesParams {
  from?: string;
  to?: string;
  projectId?: number;
  sourceId?: number;
  type?: number;
  sourceApp?: string;
  search?: string;
  page?: number;
  limit?: number;
}

// "profit" - суми, виведені на користь людини як прибуток (не entries).
export interface FinanceLegacyProfit {
  id: number;
  sourceId: number | null;
  currencyId: number | null;
  amount: number | null;
  amountUah: number | null;
  amountUsd: number | null;
  createdUserId: number | null;
  createdAt: string | null;
  updatedAt: string | null;
  isDeleted: number | null;
  sourceApp: string;
}

export interface GetProfitParams {
  from?: string;
  to?: string;
  sourceId?: number;
  sourceApp?: string;
  page?: number;
  limit?: number;
}

// Тіло створення/редагування "Профіт" (2026-08-21, по аналогії з entries) -
// без project/emp/type, той самий журнал не має цих полів.
export interface CreateProfitPayload {
  sourceId: number;
  currencyId: number;
  amount: number;
  createdUserId: number;
  createdAt?: string;
}

export type UpdateProfitPayload = Partial<CreateProfitPayload>;

// Повний рядок entry_currencies (не відфільтрований {value,label} з
// options.currencies) - для вкладки "Валюти", де користувач хоче бачити
// всі 174 рядки, а не лише активні (rate заповнений).
export interface FinanceLegacyCurrency {
  id: number;
  caption: string;
  description: string;
  rate: number | null;
  rateDate: string | null;
}

// Список юзерів старого інтерфейсу - лише id/name/role/color, беку
// email/password/remember_token не читає взагалі (User entity).
export interface FinanceLegacyUser {
  id: number;
  name: string;
  role: string | null;
  color: string | null;
}

// Усе на базі USD (2026-08-21, уточнення користувача - "грн не цікава",
// USD - основна обовʼязкова цифра). byProject/bySource/bySourceApp/byYear -
// "різноманітніша" статистика за проханням користувача.
export interface FinanceLegacyStatsBreakdown {
  count: number;
  incomeUsd: number;
  expenseUsd: number;
  netUsd: number;
}

export interface FinanceLegacyStats {
  totalEntries: number;
  totalIncomeUsd: number;
  totalExpenseUsd: number;
  netUsd: number;
  byProject: (FinanceLegacyStatsBreakdown & { projectId: number | null; label: string })[];
  bySource: (FinanceLegacyStatsBreakdown & { sourceId: number | null; label: string })[];
  bySourceApp: (FinanceLegacyStatsBreakdown & { sourceApp: string; label: string })[];
  byYear: (FinanceLegacyStatsBreakdown & { year: number })[];
}

// "Статистика 2" (2026-08-21, за проханням користувача) - витрати (джерело
// "Дима") vs профіт (те саме джерело), по роках, USD - "логіка профіту
// старої системи" (загальна кількість вкладень vs виведених коштів).
export interface FinanceLegacyStats2 {
  totalExpenseUsd: number;
  totalProfitUsd: number;
  totalDiffUsd: number;
  byYear: { year: number; expenseUsd: number; profitUsd: number; diffUsd: number }[];
}

export const financeLegacyService = {
  getEntries: async (params: GetEntriesParams): Promise<{ data: FinanceLegacyEntry[]; meta: { total: number; page: number; limit: number } }> => {
    const res = await api.get('/finance-legacy/entries', { params });
    return { data: res.data.data, meta: res.data.meta };
  },

  // FIX (2026-08-20): контролер повертає голий об'єкт, але глобальний
  // ResponseInterceptor (api/src/common/interceptors/response.interceptor.ts)
  // все одно обгортає БУДЬ-яку відповідь без власного поля `success` у
  // {success, data, meta} - тому реальні дані лежать у res.data.data, не
  // в res.data. Той самий інтерцептор діє на всі options-ендпоінти проєкту.
  getOptions: async (): Promise<FinanceLegacyOptions> => {
    const res = await api.get('/finance-legacy/options');
    return res.data.data;
  },

  getProfit: async (params: GetProfitParams): Promise<{ data: FinanceLegacyProfit[]; meta: { total: number; page: number; limit: number } }> => {
    const res = await api.get('/finance-legacy/profit', { params });
    return { data: res.data.data, meta: res.data.meta };
  },

  // /stats - той самий інтерцептор-нюанс, що й /options: res.data.data.
  // sourceId? (2026-08-21) - фільтр по джерелу для вкладки "Статистика".
  getStats: async (sourceId?: number): Promise<FinanceLegacyStats> => {
    const res = await api.get('/finance-legacy/stats', { params: { sourceId } });
    return res.data.data;
  },

  // Той самий інтерцептор-нюанс: res.data.data.
  getStats2: async (): Promise<FinanceLegacyStats2> => {
    const res = await api.get('/finance-legacy/stats2');
    return res.data.data;
  },

  // Повний список валют (усі 174, без фільтра по rate) - для вкладки
  // "Валюти". Той самий інтерцептор-нюанс: res.data.data.
  getCurrencies: async (): Promise<FinanceLegacyCurrency[]> => {
    const res = await api.get('/finance-legacy/currencies');
    return res.data.data;
  },

  // Той самий інтерцептор-нюанс: res.data.data.
  getUsers: async (): Promise<FinanceLegacyUser[]> => {
    const res = await api.get('/finance-legacy/users');
    return res.data.data;
  },

  createEntry: async (payload: CreateEntryPayload): Promise<FinanceLegacyEntry> => {
    const res = await api.post('/finance-legacy/entries', payload);
    return res.data.data;
  },

  // Бек кине 403, якщо запис не sourceApp=WEB (див. FinanceLegacyService.updateEntry).
  updateEntry: async (id: number, payload: UpdateEntryPayload): Promise<FinanceLegacyEntry> => {
    const res = await api.patch(`/finance-legacy/entries/${id}`, payload);
    return res.data.data;
  },

  // Автопідстановка юзера легасі-бази за email залогіненого адміна (дефолт
  // форми, не примус) - той самий інтерцептор-нюанс: res.data.data.
  getMyLegacyUserMapping: async (): Promise<{ legacyUserId: number | null }> => {
    const res = await api.get('/finance-legacy/users/my-mapping');
    return res.data.data;
  },

  // Soft-delete (is_deleted=1). Бек кине 403, якщо запис не sourceApp=WEB.
  deleteEntry: async (id: number): Promise<{ id: number }> => {
    const res = await api.delete(`/finance-legacy/entries/${id}`);
    return res.data.data;
  },

  createProfit: async (payload: CreateProfitPayload): Promise<FinanceLegacyProfit> => {
    const res = await api.post('/finance-legacy/profit', payload);
    return res.data.data;
  },

  // Бек кине 403, якщо запис не sourceApp=WEB.
  updateProfit: async (id: number, payload: UpdateProfitPayload): Promise<FinanceLegacyProfit> => {
    const res = await api.patch(`/finance-legacy/profit/${id}`, payload);
    return res.data.data;
  },

  // Soft-delete (is_deleted=1). Бек кине 403, якщо запис не sourceApp=WEB.
  deleteProfit: async (id: number): Promise<{ id: number }> => {
    const res = await api.delete(`/finance-legacy/profit/${id}`);
    return res.data.data;
  },
};
